import { allEapSessionRequestsQuery, isAnyCountryEapEnabled, supporterEAPSettingsQuery } from "@/api/supporterEap";
import { AppLayout } from "@/components/layout/AppLayout";
import {
  NavigationMenu,
  NavigationMenuHeader,
  NavigationMenuHeading,
  NavigationMenuLink,
  SupporterNavigationMenuItems,
} from "@/components/layout/NavigationMenu";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { MoneyFnsProvider } from "@/modules/money-fns";
import { useIsUpdateAvailable } from "@/utils/check-for-updates";
import { checkAuth, checkSupporterRole } from "@/utils/router";
import { Center, Spinner } from "@givenwell/components";
import {
  BagDollarIcon,
  ChatBubbleOvalIcon,
  CogIcon,
  CreditCardIcon,
  GraphIcon,
  HealthcareIcon,
  HomeIcon,
  PadlockIcon,
  UsersIcon,
} from "@givenwell/core-solid-icons";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";

export const Route = createFileRoute("/supporter/$supporterId")({
  loader: async ({ params }) => {
    await checkAuth();
    await checkSupporterRole(params.supporterId);
  },
  component: Layout,
});

function Layout() {
  const { supporterId } = Route.useParams();
  const { data: eapData } = useQuery(supporterEAPSettingsQuery(supporterId));

  const sidebar = useMatches({
    select(matches) {
      return matches?.findLast(match => match.context.sidebar)?.context.sidebar;
    },
  });

  const updateAvailable = useIsUpdateAvailable();

  const { data } = useQuery(
    allEapSessionRequestsQuery({
      supporterId,
      status: "pending",
      page: 1,
      pageSize: 1,
    }),
  );

  return (
    <AppLayout
      sidebar={
        sidebar || (
          <NavigationMenu header={<NavigationMenuHeader />} footer={<SupporterNavigationMenuItems />}>
            <NavigationMenuLink
              params={{ supporterId }}
              to="/supporter/$supporterId"
              icon={<HomeIcon />}
              activeOptions={{ exact: true }}
              reloadDocument={updateAvailable}
            >
              Dashboard
            </NavigationMenuLink>

            {eapData && isAnyCountryEapEnabled(eapData) && (
              <>
                <NavigationMenuHeading>EAP</NavigationMenuHeading>

                <NavigationMenuLink
                  to="/supporter/$supporterId/eap/settings"
                  params={{ supporterId }}
                  icon={<HealthcareIcon />}
                  reloadDocument={updateAvailable}
                >
                  EAP Settings
                </NavigationMenuLink>
                <NavigationMenuLink
                  to="/supporter/$supporterId/eap"
                  activeOptions={{ exact: true }}
                  params={{ supporterId }}
                  icon={<GraphIcon />}
                  reloadDocument={updateAvailable}
                >
                  EAP Reports
                </NavigationMenuLink>
                <NavigationMenuLink
                  to="/supporter/$supporterId/eap/requests"
                  params={{ supporterId }}
                  icon={<ChatBubbleOvalIcon />}
                  reloadDocument={updateAvailable}
                  badge={data?.total_items}
                >
                  Top-up Requests
                </NavigationMenuLink>
              </>
            )}

            <NavigationMenuHeading>Wellbeing Allowances</NavigationMenuHeading>

            <NavigationMenuLink
              to="/supporter/$supporterId/allowances"
              params={{ supporterId }}
              icon={<BagDollarIcon />}
              reloadDocument={updateAvailable}
            >
              Manage Allowances
            </NavigationMenuLink>
            <NavigationMenuLink
              to="/supporter/$supporterId/allowance-reports"
              params={{ supporterId }}
              icon={<GraphIcon />}
              reloadDocument={updateAvailable}
            >
              Allowance Reports
            </NavigationMenuLink>

            <NavigationMenuHeading>Admin</NavigationMenuHeading>

            <NavigationMenuLink
              to="/supporter/$supporterId/team"
              params={{ supporterId }}
              icon={<UsersIcon />}
              reloadDocument={updateAvailable}
            >
              Manage Staff
            </NavigationMenuLink>
            <NavigationMenuLink
              to="/supporter/$supporterId/financial-reports"
              params={{ supporterId }}
              icon={<CreditCardIcon />}
              reloadDocument={updateAvailable}
            >
              Billing
            </NavigationMenuLink>
            <NavigationMenuLink
              to="/supporter/$supporterId/admins"
              params={{ supporterId }}
              icon={<PadlockIcon />}
              reloadDocument={updateAvailable}
            >
              Admin Users
            </NavigationMenuLink>
            <NavigationMenuLink
              to="/supporter/$supporterId/settings"
              params={{ supporterId }}
              icon={<CogIcon />}
              reloadDocument={updateAvailable}
            >
              Settings
            </NavigationMenuLink>
          </NavigationMenu>
        )
      }
      role="supporter"
    >
      <MoneyFnsProvider
        fallback={
          <PageWrapper>
            <Center css={{ flex: "1 0 0px" }}>
              <Spinner />
            </Center>
          </PageWrapper>
        }
      >
        <Outlet />
      </MoneyFnsProvider>
    </AppLayout>
  );
}
